import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Eye,
  MailCheck,
  MessageSquareReply,
  MousePointerClick,
  Users,
} from 'lucide-react';

import { analyticsApi } from '@/api/misc';
import { ErrorState } from '@/components/ui/error-state';
import { KpiCard } from '@/components/ui/kpi-card';
import { Loader } from '@/components/ui/loader';
import { Topbar } from '@/components/ui/topbar';
import { qk } from '@/lib/query-keys';

export function DashboardPage(): JSX.Element {
  const summary = useQuery({
    queryKey: qk.analytics.summary,
    queryFn: () => analyticsApi.summary(),
  });

  return (
    <>
      <Topbar title="Dashboard" description="At-a-glance for this location." />
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {summary.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : summary.isError ? (
          <ErrorState onRetry={() => void summary.refetch()} />
        ) : summary.data ? (
          (() => {
            const s = summary.data;
            const pct = (n: number) =>
              s.sent > 0 ? `${((n / s.sent) * 100).toFixed(1)}%` : '—';
            return (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard label="Total leads" value={s.total_leads} icon={Users} />
                <KpiCard label="Sent" value={s.sent} icon={MailCheck} />
                <KpiCard
                  label="Opened"
                  value={s.opened}
                  icon={Eye}
                  delta={{ value: pct(s.opened), direction: s.opened > 0 ? 'up' : 'flat' }}
                />
                <KpiCard
                  label="Clicked"
                  value={s.clicked}
                  icon={MousePointerClick}
                  delta={{ value: pct(s.clicked), direction: s.clicked > 0 ? 'up' : 'flat' }}
                />
                <KpiCard
                  label="Replied"
                  value={s.replied}
                  icon={MessageSquareReply}
                  delta={{ value: pct(s.replied), direction: s.replied > 0 ? 'up' : 'flat' }}
                />
                <KpiCard
                  label="Bounced"
                  value={s.bounced}
                  icon={AlertTriangle}
                  delta={{
                    value: pct(s.bounced),
                    direction: s.sent > 0 && s.bounced / s.sent > 0.03 ? 'down' : 'flat',
                  }}
                />
              </div>
            );
          })()
        ) : null}

        <div className="mb-card p-6 text-sm text-mb-text-muted">
          <div className="font-display font-semibold text-mb-text mb-2">Getting started</div>
          <ol className="list-decimal ml-5 space-y-1">
            <li>Connect a Gmail Workspace mailbox on the Mailboxes page.</li>
            <li>Upload leads (CSV or auto-pull) on the Leads page.</li>
            <li>Design a campaign and launch it.</li>
            <li>Watch replies land in the Inbox — Markdown Marty classifies each one.</li>
          </ol>
        </div>
      </div>
    </>
  );
}
